<template>
  <div ref="buttonSplit" :class="splitButtonClasses">
    <button
      :class="primaryButtonClasses"
      :disabled="isDisabled"
      @click="handlePrimaryClick"
    >
      <div v-if="showLeftIcon" :class="leftIconClasses">&nbsp;</div>
      <div v-if="showText" :class="textClasses">
        {{ text }}
      </div>
    </button>
    <menu-wrapper 
      :class="menuWrapperClasses" 
      :disabled="isDisabled"
    >
      <template #menu-button="{toggleMenu}">
        <button
          :class="menuButtonClasses"
          :disabled="isDisabled"
          @click.prevent="toggleMenu"
        ></button>
      </template>
      <template #menu-content>
        <menu-item
          v-for="(item, index) in menuItems"
          :key="index"
          :seperator-bottom="item.seperatorBottom"
          @menu-item-click="clickItem(item)"
          >{{ item.text }}</menu-item
        >
      </template>
    </menu-wrapper>
  </div>
</template>

<script>
export default {
  name: 'MsSplitButton'
}
</script>

<script setup>
import { ref } from 'vue'
import MenuWrapper from '@/components/dropdown/MenuWrapper.vue'
import MenuItem from '@/components/dropdown/MenuItem.vue'

// Composables
import { useSplitButton } from './split-button/composables/useSplitButton.js'
import { useSplitButtonEvents } from './split-button/composables/useSplitButtonEvents.js'
import { useSplitButtonStyles } from './split-button/composables/useSplitButtonStyles.js'

// Constants
import { DEFAULT_PROPS, VALID_BUTTON_TYPES, SPLIT_BUTTON_EVENTS } from './split-button/constants/split-button-constants.js'

// Props - giữ nguyên 100% từ file gốc, bao gồm cả typo
const props = defineProps({
  // Text hiển thị
  text: {
    defualt: DEFAULT_PROPS.text, // Giữ nguyên typo từ gốc
    type: String,
  },
  // Icon trái
  leftIcon: {
    default: DEFAULT_PROPS.leftIcon,
    type: String,
  },
  // Icon phải
  rightIcon: {
    default: DEFAULT_PROPS.rightIcon,
    type: String,
  },
  // Loại button gồm 3 loại primary/secondary/third/four để hiện thị style tương ứng
  type: {
    default: DEFAULT_PROPS.type,
    type: String,
    validator: (value) => VALID_BUTTON_TYPES.includes(value)
  },
  /**
   * Trạng thái button disabled
   */
  disabled: {
    default: DEFAULT_PROPS.disabled,
    type: Boolean,
  },
  /**
   * Danh sách các item của context menu
   */
  menuItems: {
    type: Array,
    default: () => DEFAULT_PROPS.menuItems,
  },
  /**
   * ĐVThi 18/02/2020
   * muốn đặt thêm stype cho lớp mục đích có cách để trên form tìm được đến control
   */
  classCustom: {
    default: DEFAULT_PROPS.classCustom,
    type: String,
  },
  /**
   * NNLAM 26/02/2020
   * có cho phép tự custom vị trí dropdown menu hay không
   */
  customPositionMenu: {
    type: Boolean,
    default: DEFAULT_PROPS.customPositionMenu,
  },
  /**
   * NNLAM 26/02/2020
   * Set lại chiều dài menu dropdown
   */
  customWidth: {
    type: Number,
    default: DEFAULT_PROPS.customWidth,
  },
  /**
   * NNLAM 26/02/2020
   * khoảng cách của button so với lề trên/dưới
   */
  heightOffset: {
    type: Number,
    default: DEFAULT_PROPS.heightOffset,
  },
  /**
   * NNLAM 26/02/2021
   * khoảng cách của button so với lề trái/phải
   */
  widthOffset: {
    type: Number,
    default: DEFAULT_PROPS.widthOffset,
  },
  /**
   * NNLAM 26/02/2021
   * Có show menu khi click vào icon right của button không, mặc định có
   */
  hasShowMenu: {
    type: Boolean,
    default: DEFAULT_PROPS.hasShowMenu,
  },
  /**
   * NNLAM 26/02/2021
   * Có tooltip cho btt không
   */
  tooltipText: {
    type: [String, Object],
    default: DEFAULT_PROPS.tooltipText,
  },
  // border hiển thị cho các button con
  hasBorder: {
    default: DEFAULT_PROPS.hasBorder,
    type: Boolean,
  },
  // border bên ngoài 2 button con
  hasBorderParent: {
    default: DEFAULT_PROPS.hasBorderParent,
    type: Boolean,
  },
})

// Events
const emit = defineEmits(SPLIT_BUTTON_EVENTS)

// Template refs
const buttonSplit = ref(null)

// Composables
const splitButtonState = useSplitButton(props)
const { handlePrimaryClick, clickItem } = useSplitButtonEvents(props, emit)
const {
  splitButtonClasses,
  primaryButtonClasses,
  leftIconClasses,
  textClasses,
  menuWrapperClasses,
  menuButtonClasses,
  showLeftIcon,
  showText
} = useSplitButtonStyles(props, splitButtonState)

// Destructure state for template
const { isDisabled } = splitButtonState

// Expose API - giữ nguyên toggleMenu ref từ gốc và các state cần thiết
defineExpose({
  buttonSplit,
  isDisabled,
  handlePrimaryClick,
  clickItem
})
</script>

<style scoped lang="scss">
@import '@/assets/scss/components/MsButton.scss';

.split-icon {
  width: 36px;
  height: 24px;
  margin: 0 !important;
  .icon {
    margin: 0;
    cursor: pointer;
  }
}
.widget-more-container{
  height: 28px;
}
.context-item {
  height: 36px;
  padding: 2px 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  .context-text {
    padding: 0 8px;
  }
  &:hover {
    cursor: pointer;
    background: $hover-bg;
    color: #14aa8a;
  }
}
.context-icon {
  margin-right: 8px;
}
.color-red {
  color: #ff0000;
}
.button-line {
  display: inline-block;
  width: 1px;
  height: 16px;
  background-color: #fff;
}
.split-button {
  // background-color: $primary;
  &.border-split-btn {
    border: 1px solid #afafaf;
    border-radius: 3px;
  }
  button {
    &.primary-button {
      border-end-end-radius: unset;
      border-start-end-radius: unset;
      &:focus-visible {
        z-index: 1;
      }
    }
  }
}
</style>
